import type { DocumentTypeEntry } from '@/types/bundle'

// "+ New Document Type"'s starting shape - intentionally schema-incomplete
// (one empty required-keyword group, page 1) since it's a real unsaved draft
// the operator fills in, not a finished config. Save is blocked client-side
// while a required group is still empty (Frontend PRD 03 States).
export const EMPTY_DOCUMENT_TYPE: DocumentTypeEntry = {
  identification: {
    method: 'keyword',
    required_keywords_any_of: [[]],
    supporting_keywords: [],
    min_supporting_matches: 0,
    search_pages: [1],
  },
  fields: {},
}

export const PDF_ACCEPT = 'application/pdf'

// "Duplicate" pre-fills identification and fields in full, response_tag
// included verbatim (PRD 35 - previously blanked, matching PRD 04's
// single-field Duplicate rule, but that rule only holds *within one
// document type*: response_tag uniqueness is enforced per document type
// (app/engines/extraction_engine.py::_resolve_fields builds a fresh
// field_id_by_response_tag map per call), and output_resolution never
// references response_tag at all, only the field key - confirmed, zero
// cross-document-type or output_resolution significance. A whole-document
// Duplicate creates a brand-new document_type_id, so copying tags verbatim
// here cannot collide with anything, anywhere. This also removes the real
// bug the old blank-tag behaviour caused: every copied field keeps its
// original (non-synthetic) key, which the Fields tab always renders
// "locked" (isLocked={!isSyntheticFieldKey(key)}) - a locked field's
// response_tag input is disabled and its "required" error is suppressed
// (FieldRow.tsx), so a blanked tag on a locked key was invisible and
// unfixable until Kashif's real incident reached production. Copying the
// tag verbatim means no field is ever blank here in the first place.
// prompt.template_file is still cleared, for an unrelated reason:
// extraction.prompt_template_file (product-types side) must never point
// two document types at the same underlying prompt file. mode travels with
// the clone unchanged (Frontend PRD 03's own Duplicate requirement), only
// template_file is reset; the detail screen's onConfirm handler fetches the
// source's prompt content separately and seeds it into the new (unsaved)
// form.
export function cloneForDuplicate(source: DocumentTypeEntry): DocumentTypeEntry {
  const cloned: DocumentTypeEntry = structuredClone(source)
  if (cloned.prompt) {
    cloned.prompt = { ...cloned.prompt, template_file: '' }
  }
  return cloned
}

// Deterministic, never operator-visible or -editable - matches PRD 29's
// prompt_files.py filename validation (^[A-Za-z0-9_-]+\.txt$ exactly, no "/"
// at all, since the route is already scoped under prompts/). Real document
// type IDs (e.g. "memorandum_of_sale") already satisfy this.
export function deriveDocumentPromptFilename(documentTypeId: string): string {
  return `document_${documentTypeId}_prompt.txt`
}
